import React, { useState, useEffect } from 'react';
import {
  DAY_BITS,
  JS_DAY_TO_BIT,
  cycleToBitmask,
  bitmaskToCycle,
  cycleToFriendlyLabel,
  getNextOccurrence,
  inventoryPeriodConfigToCycle,
} from './cycleHelpers.mjs';

export { cycleToBitmask, bitmaskToCycle, cycleToFriendlyLabel, getNextOccurrence, inventoryPeriodConfigToCycle };

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// Grouped presets — rendered as <optgroup> for visual hierarchy.
// "Custom…" is intentionally placed last (after all preset groups).
const PRESET_GROUPS = [
  {
    label: 'Daily',
    options: [
      { value: 'every_day', label: 'Every day' },
    ],
  },
  {
    label: 'Weekly',
    options: [
      { value: 'every_monday',    label: 'Every Monday' },
      { value: 'every_tuesday',   label: 'Every Tuesday' },
      { value: 'every_wednesday', label: 'Every Wednesday' },
      { value: 'every_thursday',  label: 'Every Thursday' },
      { value: 'every_friday',    label: 'Every Friday' },
      { value: 'every_saturday',  label: 'Every Saturday' },
      { value: 'every_sunday',    label: 'Every Sunday' },
      { value: 'weekly_choice',   label: 'Weekly — pick days' },
      { value: 'twice_weekly',    label: 'Twice a week' },
    ],
  },
  {
    label: 'Monthly',
    options: [
      { value: 'monthly_1',    label: '1st of the month' },
      { value: 'monthly_15',   label: '15th of the month' },
      { value: 'monthly_last', label: 'Last day of the month' },
    ],
  },
];

const CUSTOM_OPTION = { value: 'custom', label: 'Custom…' };

const EOP_PRESET_GROUP = {
  label: 'NRF Fiscal Period End',
  options: [
    { value: 'eop_454', label: '4-5-4 calendar' },
    { value: 'eop_544', label: '5-4-4 calendar' },
    { value: 'eop_445', label: '4-4-5 calendar' },
  ],
};

const dayBtnStyle = (sel) => ({
  padding: '0.2rem 0.5rem',
  fontSize: '0.78rem',
  borderRadius: '4px',
  border: `1px solid ${sel ? '#5A4A42' : '#C8B5A0'}`,
  background: sel ? '#5A4A42' : '#fff',
  color: sel ? '#fff' : '#5A4A42',
  cursor: 'pointer',
  transition: 'all 0.15s',
});

const DayToggleRow = ({ selected, onChange, limit }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
    {DAY_NAMES.map(day => {
      const sel = (selected || []).includes(day);
      const count = (selected || []).length;
      return (
        <button key={day} type="button"
          style={dayBtnStyle(sel)}
          onClick={() => {
            if (sel) {
              onChange((selected || []).filter(d => d !== day));
            } else if (!limit || count < limit) {
              onChange([...(selected || []), day]);
            } else if (limit === 2) {
              onChange([(selected || [])[1], day]);
            }
          }}
        >{day.slice(0, 3)}</button>
      );
    })}
  </div>
);

const CycleSelector = ({ value, onChange, disabled = false, includeEopPresets = false, periodEndDates = null }) => {
  const [cycle, setCycle] = useState(() => value || { preset: '' });

  useEffect(() => {
    if (value && JSON.stringify(value) !== JSON.stringify(cycle)) {
      setCycle(value);
    }
  }, [JSON.stringify(value)]);

  const update = (newCycle) => {
    setCycle(newCycle);
    if (onChange) onChange(newCycle);
  };

  const handlePreset = (preset) => {
    const next = { preset };
    if (preset === 'every_day') {
      next.days = [...DAY_NAMES];
    } else if (preset.startsWith('every_')) {
      const name = preset.slice(6);
      next.days = [name.charAt(0).toUpperCase() + name.slice(1)];
    } else if (preset === 'weekly_choice') {
      next.days = cycle.days && cycle.days.length > 0 ? cycle.days : ['Monday'];
    } else if (preset === 'twice_weekly') {
      const prev = cycle.days || [];
      next.days = prev.length >= 2 ? prev.slice(0, 2) : ['Monday', 'Thursday'];
    } else if (preset === 'eop_454' || preset === 'eop_544' || preset === 'eop_445') {
      if (Array.isArray(periodEndDates)) next.periodEndDates = periodEndDates;
    } else if (preset === 'custom') {
      next.interval = cycle.interval || 1;
      next.unit = cycle.unit || 'weeks';
      next.weekDays = cycle.weekDays || cycle.days || ['Monday'];
      next.monthDay = cycle.monthDay || 1;
      next.monthType = cycle.monthType || 'day';
      next.ordinalPos = cycle.ordinalPos || 'first';
      next.ordinalWeekday = cycle.ordinalWeekday || 'Monday';
    }
    update(next);
  };

  const preset = cycle.preset || '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <select
        className="form-select form-select-sm"
        value={preset}
        disabled={disabled}
        onChange={e => handlePreset(e.target.value)}
        style={{ maxWidth: '280px' }}
      >
        <option value="">— Choose schedule —</option>
        {includeEopPresets && (
          <optgroup label={EOP_PRESET_GROUP.label}>
            {EOP_PRESET_GROUP.options.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </optgroup>
        )}
        {PRESET_GROUPS.map(group => (
          <optgroup key={group.label} label={group.label}>
            {group.options.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </optgroup>
        ))}
        <optgroup label="Other">
          <option value={CUSTOM_OPTION.value}>{CUSTOM_OPTION.label}</option>
        </optgroup>
      </select>

      {preset === 'weekly_choice' && (
        <div>
          <div style={{ fontSize: '0.78rem', color: '#8B7B73', marginBottom: '0.3rem' }}>Which day(s)?</div>
          <DayToggleRow
            selected={cycle.days}
            onChange={days => update({ ...cycle, days })}
          />
        </div>
      )}

      {preset === 'twice_weekly' && (
        <div>
          <div style={{ fontSize: '0.78rem', color: '#8B7B73', marginBottom: '0.3rem' }}>Choose 2 days:</div>
          <DayToggleRow
            selected={cycle.days}
            limit={2}
            onChange={days => update({ ...cycle, days })}
          />
        </div>
      )}

      {preset === 'custom' && (
        <div style={{ background: '#F5F3F0', borderRadius: '6px', padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.85rem', color: '#5A4A42' }}>Repeat every</span>
            <input
              type="number" min="1" max="99"
              value={cycle.interval || 1}
              onChange={e => update({ ...cycle, interval: Math.max(1, parseInt(e.target.value) || 1) })}
              style={{ width: '58px', padding: '0.2rem 0.4rem', fontSize: '0.85rem', border: '1px solid #C8B5A0', borderRadius: '4px' }}
            />
            <select
              value={cycle.unit || 'weeks'}
              onChange={e => update({ ...cycle, unit: e.target.value })}
              style={{ padding: '0.2rem 0.4rem', fontSize: '0.85rem', border: '1px solid #C8B5A0', borderRadius: '4px' }}
            >
              <option value="days">days</option>
              <option value="weeks">weeks</option>
              <option value="months">months</option>
            </select>
          </div>

          {(cycle.unit || 'weeks') === 'weeks' && (
            <div>
              <div style={{ fontSize: '0.78rem', color: '#8B7B73', marginBottom: '0.3rem' }}>On days:</div>
              <DayToggleRow
                selected={cycle.weekDays}
                onChange={weekDays => update({ ...cycle, weekDays })}
              />
            </div>
          )}

          {cycle.unit === 'months' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.82rem', cursor: 'pointer' }}>
                <input type="radio" name="cs-month-type-day"
                  checked={(cycle.monthType || 'day') === 'day'}
                  onChange={() => update({ ...cycle, monthType: 'day' })} />
                Day
                <input type="number" min="1" max="31" value={cycle.monthDay || 1}
                  disabled={(cycle.monthType || 'day') !== 'day'}
                  onChange={e => update({ ...cycle, monthDay: Math.max(1, Math.min(31, parseInt(e.target.value) || 1)) })}
                  style={{ width: '52px', padding: '0.15rem 0.3rem', fontSize: '0.82rem', border: '1px solid #C8B5A0', borderRadius: '4px' }} />
                of the month
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.82rem', cursor: 'pointer' }}>
                  <input type="radio" name="cs-month-type-ord"
                    checked={cycle.monthType === 'ordinal'}
                    onChange={() => update({ ...cycle, monthType: 'ordinal' })} />
                  The
                </label>
                <select value={cycle.ordinalPos || 'first'}
                  disabled={cycle.monthType !== 'ordinal'}
                  onChange={e => update({ ...cycle, ordinalPos: e.target.value })}
                  style={{ padding: '0.15rem 0.3rem', fontSize: '0.82rem', border: '1px solid #C8B5A0', borderRadius: '4px' }}>
                  <option value="first">First</option>
                  <option value="second">Second</option>
                  <option value="third">Third</option>
                  <option value="last">Last</option>
                </select>
                <select value={cycle.ordinalWeekday || 'Monday'}
                  disabled={cycle.monthType !== 'ordinal'}
                  onChange={e => update({ ...cycle, ordinalWeekday: e.target.value })}
                  style={{ padding: '0.15rem 0.3rem', fontSize: '0.82rem', border: '1px solid #C8B5A0', borderRadius: '4px' }}>
                  {DAY_NAMES.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                <span style={{ fontSize: '0.82rem', color: '#5A4A42' }}>of the month</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CycleSelector;
